function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';
import moment from 'moment-timezone';
import { isBlank } from 'underscore.string';
import { remove as removeDiacritics } from 'diacritics';
import { formatName } from 'shared/helpers/users';
import { integrationDisplayName } from 'shared/helpers/integrations';
import { defaultDurationEstimates, archiveReasons, paymentMethodsToDisplayNamesAdmin, CURBSIDE_PICKUP_SPECIAL_INSTRUCTIONS, DRIVE_THRU_PICKUP_SPECIAL_INSTRUCTIONS, SPECIAL_INSTRUCTIONS_MAX_LENGTH } from 'shared/constants';
import { getDefaultRewardsProgramDisplayName } from 'shared/core/helpers/dispensaries';
import { hasNonCashPaymentOptions } from './dispensaries';
import { formatTimeRange } from './time-utils';
import { parseBirthdayToUtcMidnight } from './date-time';
export var getCustomerEmail = function(order) {
    var _order_guestCustomer;
    var emailAddress = _.get(order, 'customer.profile.email', null);
    if (emailAddress) {
        return emailAddress;
    }
    emailAddress = _.get(order, 'customer.emails[0].address', null);
    if (emailAddress) {
        return emailAddress;
    }
    if ((_order_guestCustomer = order.guestCustomer) === null || _order_guestCustomer === void 0 ? void 0 : _order_guestCustomer.email) {
        return order.guestCustomer.email;
    }
    return null;
};
export var getCustomerNameForOrder = function(order) {
    var _order_customer;
    if ((_order_customer = order.customer) === null || _order_customer === void 0 ? void 0 : _order_customer.profile) {
        var _order_customer1;
        return formatName((_order_customer1 = order.customer) === null || _order_customer1 === void 0 ? void 0 : _order_customer1.profile);
    }
    if (order.guestCustomer) {
        return formatName(order.guestCustomer);
    }
    return 'unknown';
};
export var getCustomerPhoneForOrder = function(order) {
    var _order_customer_profile, _order_customer, _order_guestCustomer;
    if ((_order_customer = order.customer) === null || _order_customer === void 0 ? void 0 : (_order_customer_profile = _order_customer.profile) === null || _order_customer_profile === void 0 ? void 0 : _order_customer_profile.phone) {
        return order.customer.profile.phone;
    }
    if ((_order_guestCustomer = order.guestCustomer) === null || _order_guestCustomer === void 0 ? void 0 : _order_guestCustomer.phone) {
        return order.guestCustomer.phone;
    }
    return 'Unknown';
};
export var getOrderDateAndTime = function(order) {
    var _order_dispensary;
    var timezone = order.dispoTimezone || ((_order_dispensary = order.dispensary) === null || _order_dispensary === void 0 ? void 0 : _order_dispensary.timezone) || "America/Los_Angeles";
    var orderTime;
    if (_.isDate(order.createdAt)) {
        orderTime = moment.tz(order.createdAt, timezone);
    } else {
        orderTime = moment.tz(parseInt(order.createdAt, 10), timezone);
    }
    var date = orderTime.format("M/DD/YYYY");
    var time = orderTime.format("h:mm A");
    return "".concat(date, " at ").concat(time);
};
/**
 * Formats an in-store (POS) order date for display.
 *
 * Accepts a local ISO 8601 string (e.g. "2026-03-20T18:21:56.383") where the time
 * is already in the dispensary's local timezone. No timezone conversion is applied.
 *
 * @param {string | null | undefined} isoString - The local ISO date string from shipmentDeliveredLocal
 * @returns {string | null} Formatted date string (e.g. "3/20/2026 at 6:21 PM"), or null if not provided or invalid
 */ /**
 * Formats an in-store (POS) order address for display.
 *
 * @param {{ street?: string | null, city?: string | null, state?: string | null }} locationDetails
 * @returns {string | null} Formatted address (e.g. "123 Main St, Portland, OR"), or null if any field is missing
 */ export var getInstoreOrderAddress = function() {
    var _ref = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, street = _ref.street, city = _ref.city, state = _ref.state;
    var hasValidLocationDetails = !!street && !!city && !!state;
    return hasValidLocationDetails ? [
        street,
        city,
        state
    ].join(', ') : null;
};
export var getInstoreOrderDateAndTime = function(isoString) {
    if (!isoString) {
        return null;
    }
    var orderTime = moment(isoString, moment.ISO_8601);
    if (!orderTime.isValid()) {
        return null;
    }
    var date = orderTime.format("M/DD/YYYY");
    var time = orderTime.format("h:mm A");
    return "".concat(date, " at ").concat(time);
};
export var getDurationDisplayValue = function(durationMinutes) {
    var shortened = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
    if (!_.isFinite(durationMinutes)) {
        return '-';
    }
    if (durationMinutes === 0 || durationMinutes < 2) {
        return shortened ? '1m' : '1 min';
    }
    if (durationMinutes > 60) {
        return "".concat(moment.duration(durationMinutes, 'minutes').hours(), "h ").concat(durationMinutes % 60).concat(shortened ? 'm' : 'mins');
    }
    return "".concat(durationMinutes).concat(shortened ? ' m' : ' mins');
};
export var getTimestampInfo = function(order) {
    var _order_dispensary;
    var timezone = order.dispoTimezone || ((_order_dispensary = order.dispensary) === null || _order_dispensary === void 0 ? void 0 : _order_dispensary.timezone) || 'America/Los_Angeles';
    var orderTime = moment.tz(order.createdAt, timezone);
    var timeDisplay;
    if (moment().diff(orderTime, 'minutes') < 1) {
        timeDisplay = "".concat(moment().diff(orderTime, 'seconds'), " seconds ago");
        if (timeDisplay.charAt(0) === '-') {
            timeDisplay = 'just now';
        }
    } else {
        var duration = moment.duration(moment().diff(orderTime, 'minutes'), 'minutes');
        if (duration.years() > 0 || duration.months() > 0) {
            timeDisplay = "> ".concat(duration.humanize());
        } else if (duration.days() > 0) {
            timeDisplay = "".concat(duration.days(), "d ").concat(duration.hours(), "h ").concat(duration.minutes(), "m ago");
        } else if (duration.hours() > 0) {
            timeDisplay = "".concat(duration.hours(), "h ").concat(duration.minutes(), "m ago");
        } else {
            timeDisplay = "".concat(duration.minutes(), " minutes ago");
        }
    }
    var estimate;
    if (order.durationEstimates) {
        estimate = order.durationEstimates[order.deliveryInfo.deliveryOption ? 'delivery' : 'pickup'];
    }
    if (!estimate) {
        estimate = defaultDurationEstimates[order.deliveryInfo.deliveryOption ? 'delivery' : 'pickup'];
    }
    var estimateCeiling = orderTime.clone().add(estimate.highInMinutes, 'minutes');
    return {
        timeDisplay: timeDisplay,
        isLate: estimateCeiling.isBefore(moment())
    };
};
export var getProductNameForLineItem = function(item, dispensary) {
    var _dispensary_ordersConfig;
    var product = item.product, _item_option = item.option, option = _item_option === void 0 ? 'N/A' : _item_option;
    if (dispensary === null || dispensary === void 0 ? void 0 : (_dispensary_ordersConfig = dispensary.ordersConfig) === null || _dispensary_ordersConfig === void 0 ? void 0 : _dispensary_ordersConfig.posItemNames) {
        var _product_POSMetaData_children, _product_POSMetaData, _product_POSMetaData1;
        if ((_product_POSMetaData = product.POSMetaData) === null || _product_POSMetaData === void 0 ? void 0 : (_product_POSMetaData_children = _product_POSMetaData.children) === null || _product_POSMetaData_children === void 0 ? void 0 : _product_POSMetaData_children.length) {
            var childProduct = _.find(product.POSMetaData.children, {
                option: option
            });
            if (childProduct && !isBlank(childProduct.canonicalName)) {
                return childProduct.canonicalName;
            }
        }
        if (!isBlank((_product_POSMetaData1 = product.POSMetaData) === null || _product_POSMetaData1 === void 0 ? void 0 : _product_POSMetaData1.canonicalName)) {
            return product.POSMetaData.canonicalName;
        }
        if (!isBlank(product.integrationKey)) {
            return product.integrationKey;
        }
    }
    return product.Name || '';
};
// receipt.details.products[] is authoritative for line pricing; order.orders[].price/basePrice
// can go stale when multiple promos apply. Falls back to the legacy fields when there's no
// matching receipt product.
export var getLineItemPrintAmount = function(item, order) {
    var _order_receipt_details, _order_receipt, _item_product, _item_product1;
    var receiptProducts = order === null || order === void 0 ? void 0 : (_order_receipt = order.receipt) === null || _order_receipt === void 0 ? void 0 : (_order_receipt_details = _order_receipt.details) === null || _order_receipt_details === void 0 ? void 0 : _order_receipt_details.products;
    var _item_product__id;
    var productId = (_item_product__id = item === null || item === void 0 ? void 0 : (_item_product = item.product) === null || _item_product === void 0 ? void 0 : _item_product._id) !== null && _item_product__id !== void 0 ? _item_product__id : item === null || item === void 0 ? void 0 : (_item_product1 = item.product) === null || _item_product1 === void 0 ? void 0 : _item_product1.id;
    // String(...) on both sides: item.product._id is a native Mongo ObjectId on the
    // api/CloudPRNT path (order fetched directly from Mongo), not yet serialized to a
    // string like it is by the time GraphQL responses reach the admin UI. Also match on
    // `option`, since the same product ordered at two options (e.g. 3.5g vs 7g) produces
    // two separate receiptProducts entries sharing one id.
    var receiptProduct = productId != null ? _.find(receiptProducts, function(p) {
        return String(p.id) === String(productId) && p.option === (item === null || item === void 0 ? void 0 : item.option);
    }) : null;
    if (receiptProduct) {
        var _receiptProduct_discounts;
        var _receiptProduct_total;
        var total = (_receiptProduct_total = receiptProduct.total) !== null && _receiptProduct_total !== void 0 ? _receiptProduct_total : 0;
        var _receiptProduct_discounts_total;
        var discountsTotal = (_receiptProduct_discounts_total = (_receiptProduct_discounts = receiptProduct.discounts) === null || _receiptProduct_discounts === void 0 ? void 0 : _receiptProduct_discounts.total) !== null && _receiptProduct_discounts_total !== void 0 ? _receiptProduct_discounts_total : 0;
        return Math.max(total - discountsTotal, 0) / 100;
    }
    var quantity = _.isFinite(item === null || item === void 0 ? void 0 : item.quantity) ? item.quantity : 1;
    if (_.isFinite(item === null || item === void 0 ? void 0 : item.price)) {
        return item.price;
    }
    var _item_basePrice;
    return ((_item_basePrice = item === null || item === void 0 ? void 0 : item.basePrice) !== null && _item_basePrice !== void 0 ? _item_basePrice : 0) * quantity;
};
export var getMedCardString = function(order) {
    var medCard = order.medicalCard || _.get(order, 'customer.profile.medicalCard', null);
    if (!medCard) {
        var orderType = order.deliveryInfo.deliveryOption ? 'delivery' : 'pickup';
        return "Will present upon ".concat(orderType);
    }
    var number = medCard.number;
    var expirationDate = medCard === null || medCard === void 0 ? void 0 : medCard.expirationDate;
    if (expirationDate) {
        return "".concat(number, " - Exp: ").concat(expirationDate);
    }
    return '';
};
export var getStatusText = function(order) {
    var reopened = order.reopened, status = order.status;
    var suffix = reopened ? ' (reopened)' : '';
    if (status === 'inTransit') {
        if (!order.deliveryInfo.deliveryOption) {
            return 'Awaiting Pickup';
        }
        return "In Transit".concat(suffix);
    }
    if (status === 'confirmed') {
        return "Confirmed".concat(suffix);
    }
    if (status === 'closed') {
        return 'Closed';
    }
    if (status === 'open') {
        return 'New';
    }
    return '';
};
export var getCustomerDOB = function(order) {
    if (order.isGuestOrder) {
        var _order_guestCustomer = order.guestCustomer, birthMonth = _order_guestCustomer.birthMonth, birthDay = _order_guestCustomer.birthDay, birthYear = _order_guestCustomer.birthYear;
        if (birthMonth && birthDay && birthYear) {
            return "".concat(birthMonth, "/").concat(birthDay, "/").concat(birthYear);
        }
        return null;
    }
    return order.customer.profile.birthday;
};
export var getCustomerBirthdayForOrder = function(order) {
    var _order_customer_profile, _order_customer;
    if ((_order_customer = order.customer) === null || _order_customer === void 0 ? void 0 : (_order_customer_profile = _order_customer.profile) === null || _order_customer_profile === void 0 ? void 0 : _order_customer_profile.birthday) {
        var parsed = parseBirthdayToUtcMidnight(order.customer.profile.birthday, {
            allowUnpaddedUsDate: true
        });
        if (parsed && parsed.isValid()) {
            return moment(parsed.format('YYYY-MM-DD'), 'YYYY-MM-DD');
        }
        return moment(order.customer.profile.birthday, 'M-D-YYYY');
    }
    if (order.guestCustomer) {
        var guestCustomer = order.guestCustomer;
        return moment("".concat(guestCustomer.birthMonth, "-").concat(guestCustomer.birthDay, "-").concat(guestCustomer.birthYear), 'M-D-YYYY');
    }
    return null;
};
export var getCustomerIdForOrder = function(order) {
    var _order_customer_profile, _order_customer, _order_guestCustomer;
    if ((_order_customer = order.customer) === null || _order_customer === void 0 ? void 0 : (_order_customer_profile = _order_customer.profile) === null || _order_customer_profile === void 0 ? void 0 : _order_customer_profile.photoId) {
        return order.customer.profile.photoId;
    }
    if ((_order_guestCustomer = order.guestCustomer) === null || _order_guestCustomer === void 0 ? void 0 : _order_guestCustomer.photoId) {
        return order.guestCustomer.photoId;
    }
    return null;
};
export var getCustomerDriversLicenceForOrder = function(order) {
    var _order_customer_profile, _order_customer;
    if ((_order_customer = order.customer) === null || _order_customer === void 0 ? void 0 : (_order_customer_profile = _order_customer.profile) === null || _order_customer_profile === void 0 ? void 0 : _order_customer_profile.driversLicense) {
        return order.customer.profile.driversLicense;
    }
    if (order.driversLicense) {
        return order.driversLicense;
    }
    return null;
};
export var getCustomerInfoFieldsForOrder = function(order) {
    var customerName = getCustomerNameForOrder(order);
    var customerPhone = getCustomerPhoneForOrder(order);
    var customerEmail = getCustomerEmail(order);
    var customerId = getCustomerIdForOrder(order);
    var customerBirthday = getCustomerBirthdayForOrder(order);
    var customerDriversLicense = getCustomerDriversLicenceForOrder(order);
    return {
        customerName: customerName,
        customerPhone: customerPhone,
        customerEmail: customerEmail,
        customerId: customerId,
        customerBirthday: customerBirthday,
        customerDriversLicense: customerDriversLicense
    };
};
/** @typedef {{ id: string | null, reason: string | null, customerMessage: string | null }} ArchiveReason  */ /**
 * @returns {ArchiveReason}
 */ export var getArchiveReason = function(_param) {
    var id = _param.id, props = _object_without_properties(_param, [
        "id"
    ]);
    return _.find(archiveReasons(props), {
        id: id
    }) || {
        id: null,
        reason: null,
        customerMessage: null
    };
};
export var getUserContext = function(context) {
    var by = context.user._id || context.user.deviceId;
    var byType = context.user._id ? 'user' : 'device';
    var agentName = context.user.agentName;
    return {
        by: by,
        byType: byType,
        agentName: agentName
    };
};
export var getScheduledOrderTime = function(param) {
    var reservation = param.reservation, timezone = param.timezone;
    if (!(reservation === null || reservation === void 0 ? void 0 : reservation.startTimeISO)) {
        return false;
    }
    var startTimeISO = reservation.startTimeISO, endTimeISO = reservation.endTimeISO, dayOfWeek = reservation.dayOfWeek, isNextDaySlot = reservation.isNextDaySlot;
    var startTimeMoment = moment.tz(startTimeISO, timezone);
    var endTimeMoment = moment.tz(endTimeISO, timezone);
    // Use the effective business day from the slot instead of calculating it
    var nowInDispensaryTimezone = moment.tz(timezone);
    var todayDayOfWeek = nowInDispensaryTimezone.format('dddd');
    var tomorrowDayOfWeek = nowInDispensaryTimezone.clone().add(1, 'day').format('dddd');
    var prefix;
    if (dayOfWeek === todayDayOfWeek) {
        prefix = 'Today';
    } else if (dayOfWeek === tomorrowDayOfWeek) {
        prefix = 'Tomorrow';
    } else {
        // For slots beyond tomorrow, show the date
        prefix = startTimeMoment.format('M/D');
    }
    var _formatTimeRange = formatTimeRange(startTimeMoment, endTimeMoment, {
        showMinutes: true,
        nextDayIndicator: '(next day)',
        format: 'h:mma',
        isNextDaySlot: isNextDaySlot
    }), start = _formatTimeRange.start, end = _formatTimeRange.end;
    return {
        dayOfWeek: dayOfWeek,
        prefix: prefix,
        startTime: start,
        endTime: end
    };
};
export var formatReward = function(reward, param) {
    var storeSettings = param.storeSettings, location = param.location;
    var _storeSettings_rewardsIntegrationConfiguration;
    if (!reward) {
        return '';
    }
    var copy = reward.copy, operator = reward.operator, value = reward.value;
    var _storeSettings_rewardsIntegrationConfiguration_rewardsProgramDisplayName;
    var rewardPrefix = (_storeSettings_rewardsIntegrationConfiguration_rewardsProgramDisplayName = storeSettings === null || storeSettings === void 0 ? void 0 : (_storeSettings_rewardsIntegrationConfiguration = storeSettings.rewardsIntegrationConfiguration) === null || _storeSettings_rewardsIntegrationConfiguration === void 0 ? void 0 : _storeSettings_rewardsIntegrationConfiguration.rewardsProgramDisplayName) !== null && _storeSettings_rewardsIntegrationConfiguration_rewardsProgramDisplayName !== void 0 ? _storeSettings_rewardsIntegrationConfiguration_rewardsProgramDisplayName : getDefaultRewardsProgramDisplayName({
        location: location
    });
    var dollarText = operator === '$' ? operator : '';
    var percentText = operator === '%' ? operator : '';
    // Catch everything that is non-numeric, 0, or falsey
    rewardPrefix += !+value ? '' : " ".concat(dollarText).concat(value).concat(percentText);
    // TODO: add "for x pts" at the end here, if available (not currently in data)
    return "".concat(rewardPrefix, " discount applied (").concat(copy, ")");
};
export var formatRewardStatus = function(reward, showRewardStatus) {
    if (!reward || !showRewardStatus) {
        return '';
    }
    var redemptionAttempts = reward.redemptionAttempts;
    var _ref = _.head(redemptionAttempts) || {}, status = _ref.status, redeemBy = _ref.redeemBy;
    if (!status || !redeemBy) {
        return '';
    }
    var redeemStatus = status === 'pending' ? '- Redeemable at' : 'by';
    return "Reward ".concat(_.capitalize(status), " ").concat(redeemStatus, " ").concat(_.upperCase(redeemBy));
};
export var getFormattedRewardData = function(param, showRewardStatus, dispensary) {
    var appliedRewards = param.appliedRewards;
    var reward = _.head(appliedRewards);
    var formattedReward = formatReward(reward, dispensary);
    var formattedRewardStatus = formatRewardStatus(reward, showRewardStatus);
    return {
        formattedReward: formattedReward,
        formattedRewardStatus: formattedRewardStatus
    };
};
// Canonical kiosk id format: lowercase letters, digits, underscores, hyphens, 1-40 chars.
// Shared by client and server (create-order resolver) so validation can't drift apart.
export var KIOSK_ID_FORMAT = /^[a-z0-9_-]{1,40}$/;
export var isValidKioskId = function(kioskId) {
    return typeof kioskId === 'string' && KIOSK_ID_FORMAT.test(kioskId);
};
/**
 * De-slugifies a kiosk id for staff-facing display, e.g. `floor-1-device-2` -> `Floor 1 Device 2`.
 * Returns null for empty/nil/non-string input.
 */ export var getKioskDisplayName = function(kioskId) {
    if (typeof kioskId !== 'string') {
        return null;
    }
    var words = kioskId.split(/[-_]+/).filter(function(word) {
        return word.length > 0;
    });
    if (words.length === 0) {
        return null;
    }
    return words.map(function(word) {
        return "".concat(word.charAt(0).toUpperCase()).concat(word.slice(1));
    }).join(' ');
};
export var cleanStringForPrinter = function(str) {
    if (!str) {
        return '';
    }
    try {
        // replace accented characters with the most similar ascii character
        var cleanedString = removeDiacritics(str);
        // replace common fraction characters with ascii
        cleanedString = _.replace(cleanedString, /¼/g, ' 1/4');
        cleanedString = _.replace(cleanedString, /½/g, ' 1/2');
        cleanedString = _.replace(cleanedString, /¾/g, ' 3/4');
        // remove any remaining non-ascii
        // eslint-disable-next-line
        cleanedString = _.replace(cleanedString, /[^\x00-\x7F]/g, '');
        return cleanedString;
    } catch (e) {
        console.error(e);
        return '';
    }
};
export var getSpecialInstructions = function(order, curbsideArrivalInfo, showRewardStatus, dispensary) {
    var curbsideMessage = "".concat(CURBSIDE_PICKUP_SPECIAL_INSTRUCTIONS, " ").concat(cleanStringForPrinter(curbsideArrivalInfo === null || curbsideArrivalInfo === void 0 ? void 0 : curbsideArrivalInfo.arrivalInformation), " ");
    var driveThruMessage = "".concat(DRIVE_THRU_PICKUP_SPECIAL_INSTRUCTIONS, " ");
    var _getFormattedRewardData = getFormattedRewardData(order, showRewardStatus, dispensary), formattedReward = _getFormattedRewardData.formattedReward, formattedRewardStatus = _getFormattedRewardData.formattedRewardStatus;
    var specialInstructions = "".concat(cleanStringForPrinter(order.specialInstructions));
    if (!isBlank(formattedRewardStatus)) {
        specialInstructions = formattedRewardStatus.concat(specialInstructions);
    }
    if (!isBlank(formattedReward)) {
        specialInstructions = "".concat(formattedReward, " ").concat(specialInstructions);
    }
    if (order.isCurbsidePickupOrder) {
        specialInstructions = curbsideMessage.concat(specialInstructions);
    }
    if (order.isDriveThruPickupOrder) {
        specialInstructions = driveThruMessage.concat(specialInstructions);
    }
    return specialInstructions.substring(0, SPECIAL_INSTRUCTIONS_MAX_LENGTH);
};
export var formatPaymentMethod = function(param, dispensary) {
    var card = param.card, paymentMethod = param.paymentMethod;
    var debitOrder = !dispensary.paymentTypesAccepted.creditCardAtDoor && !dispensary.paymentTypesAccepted.creditCardByPhone && !dispensary.paymentTypesAccepted.linx && dispensary.paymentTypesAccepted.debit; // eslint-disable-line
    // old format payment info
    if (card) {
        return card === 'cash' ? 'Cash' : "".concat(debitOrder ? 'Debit' : 'Credit', " Card");
    }
    var method = _.get(paymentMethodsToDisplayNamesAdmin, paymentMethod, paymentMethod);
    if (method === 'Credit Card' && debitOrder) {
        method = 'Debit Card';
    }
    return method;
};
var acceptsDetailedPaymentTypes = function(dispensary) {
    return dispensary.paymentTypesAccepted.payOnlineMerrco || dispensary.paymentTypesAccepted.payOnlineMoneris || dispensary.paymentTypesAccepted.payOnlineHypur || dispensary.paymentTypesAccepted.dutchiePay || dispensary.paymentTypesAccepted.payOnlineChase || dispensary.paymentTypesAccepted.rethinkPay;
};
var hasDetailedPaymentInfo = function(order) {
    return order && (!_.isEmpty(order.paysafe) || !_.isEmpty(order.hypur) || !_.isEmpty(order.moneris));
};
export var showFullPaymentOptions = function(param, dispensary) {
    var isDelivery = param.isDelivery, order = param.order;
    return isDelivery && hasNonCashPaymentOptions(dispensary) || hasDetailedPaymentInfo(order) || acceptsDetailedPaymentTypes(dispensary) || hasNonCashPaymentOptions(dispensary) && !_.isEmpty(dispensary.paymentFees);
};
export var getOrderSyncFailureText = function(param) {
    var pos = param.pos;
    var posName = integrationDisplayName(pos.name);
    return "this order couldn't be sent to ".concat(posName, ".").concat(!isBlank(pos === null || pos === void 0 ? void 0 : pos.humanReadableError) ? " ".concat(pos.humanReadableError, " ") : ' ', "The order must be entered manually into ").concat(posName, ".");
};
export var isWithinThreshold = function(param) {
    var _param_actual = param.actual, actual = _param_actual === void 0 ? 0 : _param_actual, _param_expected = param.expected, expected = _param_expected === void 0 ? 0 : _param_expected, _param_threshold = param.threshold, threshold = _param_threshold === void 0 ? 0 : _param_threshold;
    var diff = Big(Big(expected).minus(actual)).abs();
    return diff.lte(Big(threshold));
};
export var willSendOrderReadySMS = function(dispensary, order, textNotifications) {
    var deliveryInfo = order.deliveryInfo, guestCustomer = order.guestCustomer;
    var messagingSettings = dispensary.messagingSettings;
    var disableReadyForPickup = messagingSettings.disableReadyForPickup, disableStartDelivery = messagingSettings.disableStartDelivery;
    var serviceType = (deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.deliveryOption) ? "delivery" : "pickup";
    var customerCanBeNotified = textNotifications || (guestCustomer === null || guestCustomer === void 0 ? void 0 : guestCustomer.phone);
    var willSendPickupText = customerCanBeNotified && !disableReadyForPickup && serviceType !== 'delivery';
    var willSendDeliveryText = customerCanBeNotified && !disableStartDelivery && serviceType === 'delivery';
    return willSendPickupText || willSendDeliveryText;
};
